/**
 * Local undo (SPEC §6.2). A global Y.UndoManager scoped to local-origin transactions
 * powers Cmd/Ctrl+Z; each structural/destructive action also gets its own short-lived
 * UndoManager so its toast's Undo reverts exactly that action, even after more typing.
 */
import { useSyncExternalStore } from 'react'
import * as Y from 'yjs'
import { itemsMap, settingsMap } from '@terrazzo/core'
import { doc, LocalOrigin } from '../sync/doc'
import { showToast, dismissToast } from './toasts'

/** What Undo covers: the items, and the household settings an action changes with them (the Done tab's charts). */
const scope = () => [itemsMap(doc), settingsMap(doc)]

export const undoManager = new Y.UndoManager(scope(), {
  trackedOrigins: new Set<unknown>([LocalOrigin]),
  captureTimeout: 500,
})

// What each Cmd/Ctrl+Z entry was ("Moved to Tomorrow"), so Undo / Redo buttons can say.
const labels = new WeakMap<object, string>()
const last = (stack: unknown[]) => stack[stack.length - 1] as object | undefined

/** Step back through the history (Cmd/Ctrl+Z, More → Undo, the sidebar). `toast` says what was undone. */
export function undo(opts: { toast?: boolean } = {}) {
  const item = last(undoManager.undoStack)
  if (!item) return
  const label = labels.get(item)
  undoManager.undo()
  const back = last(undoManager.redoStack)
  if (back && label) labels.set(back, label)
  refresh()
  if (opts.toast) showToast(label ? `Undone: ${label}` : 'Undone', { timeout: 2500 })
}
export function redo(opts: { toast?: boolean } = {}) {
  const item = last(undoManager.redoStack)
  if (!item) return
  const label = labels.get(item)
  undoManager.redo()
  const again = last(undoManager.undoStack)
  if (again && label) labels.set(again, label)
  refresh()
  if (opts.toast) showToast(label ? `Redone: ${label}` : 'Redone', { timeout: 2500 })
}

/** For the Undo / Redo buttons: whether each can run, and what it would undo / redo. */
export interface UndoState {
  canUndo: boolean
  canRedo: boolean
  undoLabel?: string
  redoLabel?: string
}
const compute = (): UndoState => {
  const u = last(undoManager.undoStack)
  const r = last(undoManager.redoStack)
  return { canUndo: !!u, canRedo: !!r, undoLabel: u ? labels.get(u) : undefined, redoLabel: r ? labels.get(r) : undefined }
}
let undoState = compute()
const undoSubs = new Set<() => void>()
function refresh() {
  const next = compute()
  if (next.canUndo === undoState.canUndo && next.canRedo === undoState.canRedo && next.undoLabel === undoState.undoLabel && next.redoLabel === undoState.redoLabel) return
  undoState = next
  for (const f of undoSubs) f()
}
undoManager.on('stack-item-added', refresh)
undoManager.on('stack-cleared', refresh)
undoManager.on('stack-item-popped', ({ stackItem, type }: { stackItem: object; type: 'undo' | 'redo' }) => {
  // Undone here (Cmd+Z, More): that action's toast Undo must not undo it a second time.
  if (type === 'undo') {
    const i = history.findIndex((s) => s.globalItem === stackItem)
    if (i >= 0) {
      const [s] = history.splice(i, 1)
      s!.um.destroy()
      const o = offered
      if (o && o.step === s) dismissToast(o.toast)
    }
  }
  refresh()
})

export function useUndoState(): UndoState {
  return useSyncExternalStore(
    (cb) => (undoSubs.add(cb), () => undoSubs.delete(cb)),
    () => undoState,
  )
}

/**
 * Recent undoable actions, newest last. Only the newest shows as a toast; tapping its Undo
 * reverts it and offers the one before, so you can keep stepping back in time.
 */
interface Step {
  message: string
  um: Y.UndoManager
  /** The same change on the global Cmd+Z stack, dropped when this step is undone. */
  globalItem?: unknown
}
const MAX_STEPS = 20
const history: Step[] = []

let offered: { step: Step; toast: number } | null = null
function offer(step: Step, undone = false) {
  offered = { step, toast: 0 }
  offered.toast = showToast(undone ? 'Undone' : step.message, {
    ...(undone ? { detail: `Undo again: ${step.message}` } : {}),
    action: { label: 'Undo', run: () => undoStep(step) },
  })
}

function undoStep(step: Step) {
  const i = history.indexOf(step)
  if (i < 0) return
  history.splice(i, 1)
  step.um.undo()
  step.um.destroy()
  const g = step.globalItem ? undoManager.undoStack.indexOf(step.globalItem as never) : -1
  if (g >= 0) undoManager.undoStack.splice(g, 1)
  refresh()
  // Offer the previous action that can still be undone.
  while (history.length) {
    const prev = history[history.length - 1]!
    if (prev.um.canUndo()) return offer(prev, true)
    history.pop()!.um.destroy()
  }
}

/**
 * Run `fn(origin)` as one undoable action. With a toast (the default) it joins the undo
 * history and shows "message · Undo"; `toast: false` keeps it to Cmd/Ctrl+Z only.
 * `fn` must perform its Yjs writes with the given origin.
 */
export function withUndo<T>(message: string | ((r: T) => string), fn: (origin: LocalOrigin) => T, opts: { toast?: boolean } = {}): T {
  const origin = new LocalOrigin('action')
  const um = new Y.UndoManager(scope(), { trackedOrigins: new Set([origin]), captureTimeout: 0 })
  undoManager.stopCapturing()
  const before = undoManager.undoStack.length
  let result: T
  try {
    result = fn(origin)
  } catch (e) {
    um.destroy()
    throw e
  }
  undoManager.stopCapturing()
  const message$ = typeof message === 'function' ? message(result) : message
  if (undoManager.undoStack.length > before) {
    labels.set(last(undoManager.undoStack)!, message$)
    refresh()
  }
  if (!um.canUndo() || opts.toast === false) {
    um.destroy()
    return result
  }
  const step: Step = {
    message: message$,
    um,
    globalItem: undoManager.undoStack.length > before ? undoManager.undoStack[undoManager.undoStack.length - 1] : undefined,
  }
  history.push(step)
  if (history.length > MAX_STEPS) history.shift()!.um.destroy()
  offer(step)
  return result
}
